import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useCommunity } from "@contexts/CommunityContext";

import api from "@helpers/api";
import { adaptProgram } from "../helpers/apiAdapters";

export default function useMentoringPrograms() {
  const { slug } = useParams();
  const { community, loading: communityLoading } = useCommunity();
  const currentCommunity = community.getBySlug(slug);
  const communityId = currentCommunity?.id;

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (communityLoading || !communityId) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);

    (async () => {
      try {
        const { data } = await api.get(`community/${communityId}/mentoring/programs`);

        if (requestId !== requestIdRef.current) return;
        setPrograms(Array.isArray(data?.data) ? data.data.map(adaptProgram) : []);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load mentoring programs", error);
        setPrograms([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    })();
  }, [communityLoading, communityId, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  // Optimistic status change — sets locally, reverts on a 422 (e.g. no contract).
  const setProgramActive = useCallback(
    async (program, active) => {
      if (active === program.active) return;
      setPrograms((prev) =>
        prev.map((p) => (p.id === program.id ? { ...p, active } : p))
      );

      const res = await api.patch(`community/${communityId}/mentoring/programs/${program.id}`, { active });

      if (res?.response?.status >= 400) {
        setPrograms((prev) =>
          prev.map((p) => (p.id === program.id ? { ...p, active: program.active } : p))
        );
        window.setSnackbar?.(res.response.data?.message || "Não foi possível atualizar o programa");
        return;
      }
      window.setSnackbar?.(active ? "Programa publicado" : "Programa despublicado");
    },
    [communityId]
  );

  const deleteProgram = useCallback(
    async (program) => {
      const res = await api.delete(`community/${communityId}/mentoring/programs/${program.id}`);

      if (res?.response?.status >= 400) {
        window.setSnackbar?.(res.response.data?.message || "Não foi possível excluir o programa");
        return;
      }
      setPrograms((prev) => prev.filter((p) => p.id !== program.id));
      window.setSnackbar?.("Programa excluído");
    },
    [communityId]
  );

  // Merge a created/updated program from the editor into the list (no refetch).
  const upsertProgram = useCallback((program) => {
    if (!program?.id) return;
    setPrograms((prev) => {
      const exists = prev.some((p) => p.id === program.id);
      return exists ? prev.map((p) => (p.id === program.id ? program : p)) : [program, ...prev];
    });
  }, []);

  return {
    programs,
    loading,
    refresh,
    currentCommunity,
    setProgramActive,
    deleteProgram,
    upsertProgram,
  };
}
